import { useQuery } from "@tanstack/react-query";
import { Check, ChevronsUpDown, Mail, MapPin, Phone, Plus, User } from "lucide-react";
import React, { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import cloudflare from "@/integrations/cloudflare/client";
import { cn } from "@/lib/utils";

interface Customer {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  created_at: string;
}

interface CustomerSelectorProps {
  selectedCustomer: Customer | null;
  onCustomerSelect: (customer: Customer | null) => void;
  onCreateNewCustomer: () => void;
  hideNewCustomerButton?: boolean;
}

export function CustomerSelector({
  selectedCustomer,
  onCustomerSelect,
  onCreateNewCustomer,
  hideNewCustomerButton = false,
}: CustomerSelectorProps) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Fetch customers
  const { data: customersData, isLoading } = useQuery({
    queryKey: ["customers-for-selection"],
    queryFn: async () => {
      const response = await fetch(`${cloudflare.apiUrl}/api/v1/customers?limit=100`);
      if (!response.ok) throw new Error("Failed to fetch customers");
      const result = await response.json();
      return result.data as Customer[];
    },
  });

  // Filter customers based on search
  const filteredCustomers = useMemo(() => {
    if (!customersData) return [];
    if (!searchTerm) return customersData;

    const search = searchTerm.toLowerCase().trim();
    return customersData.filter((customer) => {
      // Create full name for searching
      const fullName = `${customer.first_name || ""} ${customer.last_name || ""}`.toLowerCase();
      const reverseFullName =
        `${customer.last_name || ""} ${customer.first_name || ""}`.toLowerCase();

      // Create a searchable string with all customer fields
      const searchableFields = [
        customer.first_name,
        customer.last_name,
        fullName,
        customer.email,
        customer.phone || "",
        customer.address || "",
        customer.city || "",
        customer.state || "",
        customer.zip_code || "",
        customer.country || "",
      ]
        .join(" ")
        .toLowerCase();

      // Check if search term matches full name, reverse name, or any field
      return (
        fullName.includes(search) ||
        reverseFullName.includes(search) ||
        searchableFields.includes(search) ||
        // Also check individual words from search term
        search
          .split(" ")
          .every((word) => searchableFields.includes(word.toLowerCase()))
      );
    });
  }, [customersData, searchTerm]);

  const formatCustomerName = (customer: Customer) => {
    return `${customer.first_name} ${customer.last_name}`;
  };

  const formatCustomerLocation = (customer: Customer) => {
    const parts = [];
    if (customer.city) parts.push(customer.city);
    if (customer.state) parts.push(customer.state);
    return parts.join(", ");
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Customer Selection
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-3">
            {/* Customer Search Dropdown */}
            <div className="flex-1">
              <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild={true}>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className="w-full justify-between"
                  >
                    {selectedCustomer ? (
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        {formatCustomerName(selectedCustomer)}
                      </div>
                    ) : (
                      "Search existing customers..."
                    )}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0">
                  <Command>
                    <CommandInput
                      placeholder="Search customers by name, email, or location..."
                      value={searchTerm}
                      onValueChange={setSearchTerm}
                    />
                    <CommandList>
                      <CommandEmpty>
                        <div className="p-4 text-center space-y-2">
                          <p className="text-sm text-muted-foreground">No customers found.</p>
                          {!hideNewCustomerButton && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setOpen(false);
                                onCreateNewCustomer();
                              }}
                            >
                              <Plus className="h-4 w-4 mr-2" />
                              Create New Customer
                            </Button>
                          )}
                        </div>
                      </CommandEmpty>
                      {filteredCustomers.length > 0 && (
                        <CommandGroup heading="Existing Customers">
                          {filteredCustomers.map((customer) => (
                            <CommandItem
                              key={customer.id}
                              value={`${customer.first_name} ${customer.last_name} ${customer.email}`}
                              onSelect={() => {
                                onCustomerSelect(
                                  selectedCustomer?.id === customer.id ? null : customer
                                );
                                setOpen(false);
                              }}
                              className={cn(
                                "flex items-start gap-3 p-3 cursor-pointer rounded-lg transition-colors",
                                "hover:bg-blue-600 hover:text-white data-[highlighted]:bg-blue-600 data-[highlighted]:text-white",
                                "[&[data-highlighted]>*]:text-white [&:hover>*]:text-white",
                                "[&[data-highlighted]_span]:text-white [&:hover_span]:text-white",
                                "[&[data-highlighted]_svg]:text-white/70 [&:hover_svg]:text-white/70",
                                selectedCustomer?.id === customer.id
                                  ? "bg-blue-100 text-blue-900 border-l-4 border-blue-500"
                                  : "text-gray-700"
                              )}
                            >
                              <Check
                                className={cn(
                                  "mt-0.5 h-4 w-4",
                                  selectedCustomer?.id === customer.id
                                    ? "opacity-100 text-blue-600"
                                    : "opacity-0"
                                )}
                              />
                              <div className="flex-1 space-y-2">
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold">
                                    {formatCustomerName(customer)}
                                  </span>
                                  <Badge
                                    variant="secondary"
                                    className="text-xs bg-gray-100 text-gray-600 border-0 group-hover:bg-white/20 group-hover:text-white"
                                  >
                                    ID: {customer.id}
                                  </Badge>
                                </div>
                                <div className="space-y-1">
                                  <div className="flex items-center gap-1 text-sm">
                                    <Mail className="h-3 w-3 opacity-70" />
                                    <span>{customer.email}</span>
                                  </div>
                                  <div className="flex items-center gap-4 text-sm">
                                    {customer.phone && (
                                      <div className="flex items-center gap-1">
                                        <Phone className="h-3 w-3 opacity-70" />
                                        <span>{customer.phone}</span>
                                      </div>
                                    )}
                                    {formatCustomerLocation(customer) && (
                                      <div className="flex items-center gap-1">
                                        <MapPin className="h-3 w-3 opacity-70" />
                                        <span>{formatCustomerLocation(customer)}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Create New Customer Button */}
            {!hideNewCustomerButton && (
              <Button
                variant="outline"
                onClick={onCreateNewCustomer}
                className="flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                New Customer
              </Button>
            )}
          </div>

          {/* Clear Selection */}
          {selectedCustomer && (
            <div className="flex items-center justify-between p-3 border rounded-lg bg-muted/50">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  <span className="font-medium">{formatCustomerName(selectedCustomer)}</span>
                </div>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Mail className="h-3 w-3" />
                    {selectedCustomer.email}
                  </div>
                  {selectedCustomer.phone && (
                    <div className="flex items-center gap-1">
                      <Phone className="h-3 w-3" />
                      {selectedCustomer.phone}
                    </div>
                  )}
                </div>
                {(selectedCustomer.address || formatCustomerLocation(selectedCustomer)) && (
                  <div className="flex items-center gap-1 text-sm text-muted-foreground">
                    <MapPin className="h-3 w-3" />
                    {selectedCustomer.address && `${selectedCustomer.address}, `}
                    {formatCustomerLocation(selectedCustomer)}
                  </div>
                )}
              </div>
              <Button variant="ghost" size="sm" onClick={() => onCustomerSelect(null)}>
                Clear
              </Button>
            </div>
          )}

          {isLoading && (
            <div className="text-center text-sm text-muted-foreground py-4">
              Loading customers...
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
